import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Divider, Flex, Grid, Text, TextInput} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import AntibodyChainLegend from 'antibodies/antibody-map/AntibodyChainLegend';
import * as AntibodyFormatDAO from 'antibodies/format/AntibodyFormatDAO';
import VeritasHighlightedInput from 'antibodies/format/VeritasHighlightedInput';
import Antibody from 'antibodies/visualization/Antibody';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/AntibodiesAnalyticsConstants';
import notifications from 'notifications';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {AntibodyFormatGraphQL} from '../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';

export interface AntibodyFormatEditPaneProps {
  onRequestClose: () => void;
  analyticsMetadata: AnalyticsMetadataForContext;
  /** The format whose display name is being edited. Its VERITAS name is always shown read-only. */
  existingFormat: AntibodyFormatGraphQL;
  /** Called after a successful display name update. */
  onDisplayNameUpdated?: () => Promise<void>;
}

/** Shown in the VERITAS name field for formats with no VERITAS name (e.g. built-in formats). */
export const MISSING_VERITAS_NAME_PLACEHOLDER = 'No VERITAS name';

export default function AntibodyFormatEditPane({
  onRequestClose,
  analyticsMetadata,
  existingFormat,
  onDisplayNameUpdated,
}: AntibodyFormatEditPaneProps): JSX.Element {
  const {classes} = useStyles();
  const [formatName, setFormatName] = useState(existingFormat.name);
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveDisplayName = useCallback(async (): Promise<void> => {
    setIsSaving(true);
    try {
      await AntibodyFormatDAO.updateAntibodyFormatDisplayName(
        existingFormat.id,
        formatName.trim(),
        analyticsMetadata
      );
      notifications.success('Display name updated successfully.');
      await onDisplayNameUpdated?.();
      onRequestClose();
    } catch (e) {
      notifications.error('Failed to update display name.');
      throw e;
    } finally {
      setIsSaving(false);
    }
  }, [analyticsMetadata, existingFormat.id, formatName, onDisplayNameUpdated, onRequestClose]);

  return (
    <div
      className={classes.container}
      {...getTestProps({component: 'AntibodyFormatEditPane', element: 'root'})}
    >
      <div className={classes.header}>
        <div className={classes.headerTitle}>Edit display name</div>
        <div className={classes.headerButtons}>
          <Button
            onClick={onRequestClose}
            text="Cancel"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.SMALL}
          />
          <Button
            onClick={handleSaveDisplayName}
            text={isSaving ? 'Saving...' : 'Save'}
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.SMALL}
            disabled={isSaving || formatName.trim().length === 0}
            dataTestAttrs={{component: 'AntibodyFormatEditPane', element: 'saveButton'}}
          />
        </div>
      </div>
      <Divider />
      <Grid gutter="md">
        <Grid.Col span={6} p="xl">
          <Flex justify="space-between" align="center" mb="12px" gap="8px">
            <Text className={classes.veritasHeader}>Update the display name for this format</Text>
          </Flex>
          <FormGroup>
            <FieldLabel label="VERITAS name" htmlFor="veritas-name" className={classes.veritasNameLabel} />
            <VeritasHighlightedInput
              id="veritas-name"
              value={existingFormat.originalVeritas ?? MISSING_VERITAS_NAME_PLACEHOLDER}
              readOnly={true}
              disabled={true}
            />
            <FieldLabel label="Display name" htmlFor="display-name" />
            <TextInput
              size="md"
              id="display-name"
              onChange={(e) => {
                setFormatName(e.target.value);
              }}
              placeholder="Enter name"
              value={formatName}
              onBlur={() => {
                analytics.trackEvent(ANTIBODIES_ANALYTICS_ACTIONS.TYPED_CUSTOM_FORMAT_DISPLAY_NAME, {
                  ...analyticsMetadata,
                  display_name: formatName,
                });
              }}
            />
          </FormGroup>
        </Grid.Col>
        <Grid.Col span={6} p="xl">
          <div className={classes.previewPanel}>
            <div
              {...getTestProps({
                component: 'AntibodyFormatEditPane',
                element: 'preview-container',
              })}
            >
              <div className={classes.chainLegendWrapper}>
                <AntibodyChainLegend antibodyFormat={existingFormat} />
              </div>
              <Antibody format={existingFormat} showLabels={true} showPopovers={true} />
            </div>
          </div>
        </Grid.Col>
      </Grid>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    border: `1px solid ${theme.colors.gray[2]}`,
    borderRadius: theme.radius.sm,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.md,
    height: '52px',
  },
  headerTitle: {
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.other.fontWeights.semibold,
  },
  headerButtons: {
    display: 'flex',
    gap: theme.spacing.md,
  },
  veritasHeader: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[9],
  },
  veritasNameLabel: {
    flexGrow: 1,
  },
  previewPanel: {
    border: `1px solid ${theme.colors.gray[2]}`,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.colors.gray[0],
    padding: theme.spacing.sm,
    minHeight: '304px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  chainLegendWrapper: {
    backgroundColor: theme.white,
    fontSize: theme.fontSizes.sm,
  },
}));
